import { useMemo } from 'react';
// routes
import { paths } from 'src/routes/paths';
// locales
import { useLocales } from 'src/locales';
// components
import { Iconify } from 'src/components/iconify';
import { ENVIRONMENT } from 'src/config-global';
import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';
import {
  isRoleAdmin,
  isUserAgent,
  isUserAuditor,
  isUserCoffee,
} from 'src/utils/check-user-is-admin';
import producerPromotion from 'src/assets/icons/promotions/producer_promotion.svg';
import allProducts from 'src/assets/icons/promotions/all_products.svg';
// ----------------------------------------------------------------------

const icon = (name: string | React.ReactNode) =>
  typeof name === 'string' ? <Iconify icon={name} /> : name;

const ICONS = {
  catalog: icon(<img src={allProducts} alt="All Products" width={20} height={20} />),
  orders: icon('mdi:invoice-text-edit-outline'),
  producers: icon(<img src={producerPromotion} alt="Producer Promotion" width={16} height={16} />),
  admin: icon('mdi:archive-settings'),
  userTypes: icon('ic:outline-supervised-user-circle'),
  orderTypes: icon('ic:baseline-card-giftcard'),
  adminOrders: icon('mdi:archive-settings'),
  adminProducts: icon('mdi:format-list-bulleted-square'),
  statistics: icon('mdi:chart-line-variant'),
  tool: icon('mdi:wrench-outline'),
  agents: icon('solar:users-group-rounded-bold-duotone'),
};

// ----------------------------------------------------------------------

export function useNavData() {
  const { t } = useLocales();

  const isDevelopment = ENVIRONMENT === CONSTANT_VARIABLES.DEVELOP;
  const isProductionOrStage = new Set([
    CONSTANT_VARIABLES.STAGE,
    CONSTANT_VARIABLES.PRODUCTION,
  ]).has(ENVIRONMENT);
  const isAgent = isUserAgent();
  const isAdmin = isRoleAdmin();
  const isAuditor = isUserAuditor();
  const isCoffee = isUserCoffee();

  const statisticsMenu = useMemo(() => {
    const dashboards = [
      {
        title: t('profitability'),
        path: paths.admin.statisticTypes.profitability,
      },
      // NAT-2579 / NAT-2702 — d22 lives in Dispatcher; its BI link is gone.
      {
        title: t('sales_statistics_dispatcher'),
        path: paths.admin.statisticTypes.salesStats,
      },
      // NAT-2811 — d36 lives in Dispatcher; its BI link is gone.
      { title: t('finance'), path: paths.admin.statisticTypes.finanza },
    ];
    return [
      {
        title: t('statistics'),
        // Profitability detail pages sit on a sibling URL
        // (/admin/profitability_details/<slug>), not under /admin/profitability/,
        // so the segment-aware nav matcher needs it listed explicitly to stay lit.
        path: [
          ...dashboards.map((d) => d.path),
          paths.admin.statisticTypes.profitabilityDetailsRoot,
        ],
        icon: ICONS.statistics,
        children: dashboards,
      },
    ];
  }, [t]);

  // NAT-2788 — a coffee user sees this one page and nothing else; admins get it in
  // Mission control, the dashboards group.
  const coffeeItem = useMemo(
    () => ({
      title: t('nav_title', { ns: 'coffeeDashboard' }),
      path: paths.coffee.dashboard,
      icon: ICONS.admin,
    }),
    [t],
  );

  const customerItems = useMemo(
    () => [
      {
        title: t('catalog'),
        path: paths.customer.catalog,
        icon: ICONS.catalog,
      },
      {
        title: t('producers'),
        path: paths.customer.producers,
        icon: ICONS.producers,
      },
      {
        title: t('order_list'),
        path: paths.customer.orders.root,
        icon: ICONS.orders,
      },
    ],
    [t],
  );

  const commonAdminItems = useMemo(
    () => [
      {
        // What waits for an admin's action: the day's picture, then requests, reorders and
        // reconciliation. Client requests and the reorder sheet moved here from Clienti and
        // Produttori.
        title: t('mission_control'),
        path: [
          paths.admin.dashboard.situazione,
          paths.admin.dashboard.operations,
          paths.admin.dashboard.daIncassare,
          paths.admin.dashboard.magazzinoFermo,
          paths.admin.clients.leads,
          paths.admin.reorderSheet.root,
          paths.admin.reconciliation.root,
          paths.coffee.dashboard,
        ],
        icon: ICONS.admin,
        children: [
          // NAT-2545 — replaces the BI d34 link that used to sit under Statistiche.
          { title: t('current_situation'), path: paths.admin.dashboard.situazione },
          { title: t('operativa'), path: paths.admin.dashboard.operations },
          // NAT-2610 — the first Situazione detail page; Cassa and Da pagare follow.
          { title: t('da_incassare'), path: paths.admin.dashboard.daIncassare },
          // NAT-2612 — the second Situazione detail page.
          { title: t('magazzino_fermo'), path: paths.admin.dashboard.magazzinoFermo },
          { title: t('title', { ns: 'leadInbox' }), path: paths.admin.clients.leads },
          // NAT-2524 — its label lives in the page's own namespace, so it is read from there.
          { title: t('title', { ns: 'reorderSheetPage' }), path: paths.admin.reorderSheet.root },
          { title: t('reconciliation'), path: paths.admin.reconciliation.root },
          // NAT-2788 — Natives Coffee; the same page the coffee role lands on.
          coffeeItem,
        ],
      },
      {
        title: t('clients'),
        path: [
          paths.admin.users.root,
          paths.admin.clients.root,
          paths.admin.clients.retention,
          paths.admin.clients.scouting,
          paths.admin.companies.root,
          paths.admin.reservations.root,
          paths.admin.discounts.root,
          paths.admin.tastings.root,
          // NAT-2749 — d26 has its own URL, outside /admin/clients.
          paths.admin.clientPortfolio,
          // NAT-2781 — d31 has its own URL too.
          paths.admin.clientAnalysis,
        ],
        // The lead inbox URL sits under /admin/clients, but its entry now lives in
        // Mission control (NAT-2556) — without this, both groups light up on it.
        excludePaths: [paths.admin.clients.leads],
        icon: ICONS.userTypes,
        children: [
          { title: t('users'), path: paths.admin.users.root },
          { title: t('clients'), path: paths.admin.clients.root },
          {
            title: t('nav_title', { ns: 'retentionPage' }),
            path: paths.admin.clients.retention,
          },
          // NAT-2559 — venues found by Natives Scout, not clients yet: vetted here, and
          // possibly brought into the official database. Separate from retention on purpose.
          { title: t('nav_title', { ns: 'scoutingPage' }), path: paths.admin.clients.scouting },
          { title: t('companies'), path: paths.admin.companies.root },
          { title: t('reservations'), path: paths.admin.reservations.root },
          { title: t('discounts'), path: paths.admin.discounts.root },
          { title: t('tastings'), path: paths.admin.tastings.root },
          // NAT-2749 — d26 lives in Dispatcher; its BI dashboard is switched off (NAT-2743).
          { title: t('client_portfolio'), path: paths.admin.clientPortfolio },
          // NAT-2781 — d31 in Dispatcher; its BI dashboard is switched off.
          { title: t('client_analysis_dispatcher'), path: paths.admin.clientAnalysis },
        ],
      },
      {
        // NAT-2864 — the quality of the stock: each wine's verdict, then the producers' picture.
        title: t('stock_quality'),
        path: [paths.admin.stockQuality.vini, paths.admin.stockQuality.produttori],
        icon: ICONS.statistics,
        children: [
          { title: t('stock_quality_wines'), path: paths.admin.stockQuality.vini },
          { title: t('stock_quality_producers'), path: paths.admin.stockQuality.produttori },
        ],
      },
      {
        title: t('agents'),
        path: [paths.admin.agents.root],
        icon: ICONS.agents,
        children: [
          { title: t('agents'), path: paths.admin.agents.root },
          // NAT-2749 — d32 in Dispatcher. Admin only: the agents' menu is built elsewhere.
          { title: t('agents_analysis'), path: paths.admin.agents.analysis },
        ],
      },
      {
        title: t('orders'),
        path: [
          paths.admin.orderTypes.clientOrders.root,
          paths.admin.orderTypes.producerOrders.root,
        ],
        icon: ICONS.admin,
        children: [
          { title: t('client_orders'), path: paths.admin.orderTypes.clientOrders.root },
          { title: t('producer_orders'), path: paths.admin.orderTypes.producerOrders.root },
        ],
      },
      {
        title: t('products'),
        path: [
          paths.admin.products.root,
          paths.admin.archiveHolds.root,
          paths.admin.wineTypes.root,
          paths.admin.grapeTypes.root,
          paths.admin.selections.root,
          paths.admin.productAnalysis,
        ],
        icon: ICONS.adminProducts,
        children: [
          { title: t('all_the_products'), path: paths.admin.products.root },
          // NAT-2654 — every archived bottle, beside the stock it is held out of.
          { title: t('archive_holds'), path: paths.admin.archiveHolds.root },
          // Resolves through the shared `common` namespace, which the customer
          // catalogue and producer filters also read. Its value is deliberately
          // the general "Tipologie" / "Product types" so one string serves both
          // — the taxonomy stopped being wine-only. The key name is legacy; see
          // NAT-2363.
          { title: t('wine_types'), path: paths.admin.wineTypes.root },
          { title: t('grape_types'), path: paths.admin.grapeTypes.root },
          { title: t('selections'), path: paths.admin.selections.root },
          // NAT-2760 — d17 in Dispatcher, on the Statistiche engine.
          { title: t('product_analysis'), path: paths.admin.productAnalysis },
        ],
      },
      {
        title: t('producers'),
        path: [
          paths.admin.orderTypes.producerOrders.root,
          paths.admin.producers.root,
          paths.admin.producerFicEntities.root,
          paths.admin.producerCompanies.root,
          paths.admin.territory.root,
          paths.admin.territory.new,
          paths.admin.producerAnalysis,
        ],
        icon: ICONS.producers,
        children: [
          // Duplicated deliberately: producer orders also live under Ordini, beside client
          // orders, and belong in both places.
          { title: t('producer_orders'), path: paths.admin.orderTypes.producerOrders.root },
          { title: t('producers'), path: paths.admin.producers.root },
          // Mirrors Clienti → Aziende: the fiscal lens sits beside the trading names.
          { title: t('producer_companies'), path: paths.admin.producerCompanies.root },
          { title: t('producer_fic_entities'), path: paths.admin.producerFicEntities.root },
          { title: t('territories'), path: paths.admin.territory.root },
          // NAT-2760 — d15 in Dispatcher, on the Statistiche engine.
          { title: t('producer_analysis'), path: paths.admin.producerAnalysis },
          // The BI reorder dashboard (d33) is no longer linked: the reorder sheet under
          // Mission control replaces it entirely (NAT-2524, register row E1).
        ],
      },
      ...statisticsMenu,
      {
        title: t('tools'),
        path: [
          paths.admin.targets.root,
          paths.admin.controls.root,
          paths.admin.catalogPdf.root,
          paths.admin.dashboard.overlays.root,
          paths.admin.dashboard.table_text,
          paths.admin.dashboard.assistant,
        ],
        icon: ICONS.tool,
        children: [
          {
            title: t('targets'),
            path: paths.admin.targets.root,
          },
          {
            title: t('logistica_sync'),
            path: paths.admin.controls.root,
          },
          {
            title: t('catalog_pdf'),
            path: paths.admin.catalogPdf.root,
          },
          { title: t('overlays'), path: paths.admin.dashboard.overlays.root },
          { title: t('table_text_title'), path: paths.admin.dashboard.table_text },
          { title: t('assistant_monitor'), path: paths.admin.dashboard.assistant },
        ],
      },
    ],

    [t, statisticsMenu, coffeeItem],
  );

  const commonAgentItems = useMemo(
    () => [
      {
        title: t('dashboard'),
        path: paths.agent.dashboard,
        icon: ICONS.admin,
      },
      {
        title: t('catalog'),
        path: paths.customer.catalog,
        icon: ICONS.catalog,
      },
      {
        title: t('clients'),
        path: [paths.admin.clients.root, paths.admin.clients.new, paths.admin.clients.retention],
        icon: ICONS.userTypes,
        children: [
          { title: t('clients'), path: paths.admin.clients.root },
          {
            title: t('nav_title', { ns: 'retentionPage' }),
            path: paths.admin.clients.retention,
          },
          { title: t('new_client'), path: paths.admin.clients.new },
        ],
      },
      {
        title: t('orders'),
        path: [paths.admin.orderTypes.clientOrders.root],
        icon: ICONS.admin,

        children: [{ title: t('clients'), path: paths.admin.orderTypes.clientOrders.root }],
      },
      {
        // NAT-2795, rule 52 — what the agent's own clients still owe.
        title: t('da_incassare'),
        path: paths.admin.dashboard.daIncassare,
        icon: ICONS.admin,
      },
    ],
    [t],
  );

  const commonAuditorItems = useMemo(
    () => [
      {
        title: t('orders'),
        path: [paths.admin.orderTypes.producerOrders.root],
        icon: ICONS.admin,
        children: [{ title: t('providers'), path: paths.admin.orderTypes.producerOrders.root }],
      },
    ],
    [t],
  );
  const commonCoffeeItems = useMemo(() => [coffeeItem], [coffeeItem]);

  const developmentAdminItems = useMemo(() => commonAdminItems, [commonAdminItems]);

  const prodORStageAdminItems = useMemo(() => [...commonAdminItems], [commonAdminItems]);

  const data = useMemo(() => {
    if (isProductionOrStage && isAdmin) {
      return [
        { subheader: t('overview'), items: customerItems },
        { subheader: t('admin'), items: prodORStageAdminItems },
      ];
    }
    if (isCoffee) {
      return [{ subheader: t('overview'), items: commonCoffeeItems }];
    }
    if (isProductionOrStage && !isAdmin && !isAgent && !isAuditor) {
      return [{ subheader: t('overview'), items: customerItems }];
    }
    if (isProductionOrStage && isAgent) {
      return [{ subheader: t('overview'), items: commonAgentItems }];
    }
    if (isDevelopment && isAgent) {
      return [{ subheader: t('overview'), items: commonAgentItems }];
    }
    if (isProductionOrStage && isAuditor) {
      return [{ subheader: t('overview'), items: commonAuditorItems }];
    }
    if (isDevelopment && isAuditor) {
      return [{ subheader: t('overview'), items: commonAuditorItems }];
    }
    if (isDevelopment && isAdmin) {
      return [
        { subheader: t('overview'), items: customerItems },
        { subheader: t('admin'), items: developmentAdminItems },
      ];
    }
    return [{ subheader: t('overview'), items: customerItems }];
  }, [
    t,
    isAdmin,
    isAgent,
    isAuditor,
    isCoffee,
    isDevelopment,
    isProductionOrStage,
    customerItems,
    developmentAdminItems,
    prodORStageAdminItems,
    commonAgentItems,
    commonAuditorItems,
    commonCoffeeItems,
  ]);

  return data;
}
